import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { IChatThinkingTexts } from '@kognitos/lattice';

/**
 * Hook that provides localized thinking texts for the Chat component.
 * Centralizes the thinking text configuration used across all exception chat interfaces.
 *
 * Lives in `@/shared` rather than a feature module because the Rain runtime
 * core renders these strings too, and the core may not import a feature module
 * (RAIN_CORE_IMPORT_BAN in eslint.config.js). It is pure i18n lookup with no
 * feature state, so `@/shared` is where the ban's own message points.
 *
 * @returns {IChatThinkingTexts} Localized thinking texts for Chat component
 */
export function useThinkingTexts(): IChatThinkingTexts {
  const { t } = useTranslation('automation');

  return useMemo(
    () => ({
      title: t('automation.thinking.title'),
      completedTitle: t('automation.thinking.completedTitle'),
      completedTitleFallback: t('automation.thinking.completedTitleFallback'),
      collapseLabel: t('automation.thinking.collapseLabel'),
      expandLabel: t('automation.thinking.expandLabel'),
      showMoreLabel: t('automation.thinking.showMoreLabel'),
      showLessLabel: t('automation.thinking.showLessLabel'),
    }),
    [t],
  );
}
